import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Trophy, Star, Zap, Flame, Heart, Users, ShoppingBag, MessageSquare } from "lucide-react";

const LEVEL_ICONS = {
  bronze: { icon: Trophy, glow: "#CD7F32" },
  silver: { icon: Trophy, glow: "#C0C0C0" },
  gold: { icon: Trophy, glow: "#32D583" },
  diamond: { icon: Star, glow: "#B9F2FF" }
};

const FanLevelCard = ({ token, compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    const fetchLevel = async () => {
      try {
        const res = await axios.get(`${API}/fan-level/me/full`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data);
      } catch (e) {
        console.error("Failed to fetch fan level:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchLevel();
  }, [token]);

  if (loading) return <div className="card p-6"><div className="h-24 animate-pulse bg-[#1A2030] rounded" /></div>;
  if (!data) return null;

  const { level, level_info, xp, badges, stats, next_level } = data;
  const LevelIcon = LEVEL_ICONS[level]?.icon || Trophy;
  const glow = LEVEL_ICONS[level]?.glow || "#32D583";

  const progressPct = next_level
    ? Math.min(100, Math.round(((xp - level_info.min_xp) / (next_level.xp_needed + xp - level_info.min_xp)) * 100))
    : 100;

  if (compact) {
    return (
      <div className="flex items-center gap-3 px-4 py-2 rounded-full border" style={{ borderColor: `${glow}55`, background: `${glow}10` }} data-testid="fan-level-compact">
        <LevelIcon className="w-4 h-4" style={{ color: glow }} />
        <span className="text-sm font-bold" style={{ color: glow }}>{level_info.label}</span>
        <span className="text-xs text-[#8A92A0]">{xp} XP</span>
      </div>
    );
  }

  return (
    <div className="card p-6 relative overflow-hidden" data-testid="fan-level-card">
      {/* Glow accent */}
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: glow }} />
      <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full opacity-10 blur-3xl" style={{ background: glow }} />

      <div className="relative z-10">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${glow}20`, border: `1px solid ${glow}55` }}>
              <LevelIcon className="w-6 h-6" style={{ color: glow }} />
            </div>
            <div>
              <p className="text-xs text-[#8A92A0] uppercase tracking-wide">Your Level</p>
              <h3 className="font-['Unbounded'] text-2xl font-bold" style={{ color: glow }}>{level_info.label}</h3>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-[#8A92A0] uppercase tracking-wide">XP</p>
            <p className="text-2xl font-bold text-white">{xp.toLocaleString()}</p>
          </div>
        </div>

        {/* Progress to next level */}
        {next_level ? (
          <div className="mb-5">
            <div className="flex justify-between text-xs text-[#8A92A0] mb-1.5">
              <span>{next_level.xp_needed} XP to <span style={{ color: glow }}>{next_level.label}</span></span>
              <span>{progressPct}%</span>
            </div>
            <div className="h-2 bg-[#1A2030] rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${progressPct}%`, background: `linear-gradient(90deg, ${glow}, #86EFAC)` }} />
            </div>
          </div>
        ) : (
          <div className="mb-5 flex items-center gap-2 text-sm" style={{ color: glow }}>
            <Star className="w-4 h-4" /> Maximum level achieved
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
          {[
            { icon: ShoppingBag, label: "Purchases", value: stats.purchases, color: "#32D583" },
            { icon: Heart, label: "Following", value: stats.follows, color: "#F87171" },
            { icon: MessageSquare, label: "Posts", value: stats.posts, color: "#60A5FA" },
            { icon: Users, label: "Referrals", value: stats.referrals, color: "#C084FC" },
            { icon: Flame, label: "Streak", value: stats.streak_days, color: "#F87171" }
          ].map((s) => (
            <div key={s.label} className="bg-[#050810] rounded-lg p-2.5 border border-[#1A2030] text-center" data-testid={`fan-stat-${s.label.toLowerCase()}`}>
              <s.icon className="w-3.5 h-3.5 mx-auto mb-1" style={{ color: s.color }} />
              <p className="text-base font-bold text-white">{s.value}</p>
              <p className="text-[10px] text-[#4A5260] uppercase tracking-wide">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Badges */}
        {badges.length > 0 && (
          <div>
            <p className="text-xs text-[#8A92A0] uppercase tracking-wide mb-2 flex items-center gap-1"><Zap className="w-3 h-3" /> Badges Earned</p>
            <div className="flex flex-wrap gap-2">
              {badges.map((b) => (
                <span
                  key={b.name}
                  className="px-3 py-1 rounded-full text-xs font-bold border"
                  style={{ background: `${b.color}15`, color: b.color, borderColor: `${b.color}40` }}
                  data-testid={`badge-${b.name.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {b.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FanLevelCard;
